/**
 * Aritmética de los renglones del presupuesto.
 *
 * Vive fuera del componente porque es lo único de la pantalla que se puede
 * equivocar **en silencio**: un subtotal mal calculado se ve bien, se guarda
 * bien y recién aparece cuando el cliente compara contra la factura.
 */

/** Lo que el worker devuelve al resolver la tarifa (`GET /precios/resolver`). */
export interface PrecioResuelto {
  /** Unitario, o el total del renglón cuando `esImporteFijo`. */
  precio: string;
  /** Ya multiplicado por la cantidad — salvo importe fijo, que no se multiplica. */
  total: string;
  esImporteFijo: boolean;
  usoPrecioVip?: boolean;
}

/** Un renglón mientras se arma, con lo que hace falta para mostrar su importe. */
export interface RenglonConImporte {
  cantidad: number;
  /** Cargado a mano: no sale del catálogo y nunca lleva snapshot (ADR 0012). */
  precioUnitario?: number;
  /** `null` = la combinación no tiene tarifa vigente. */
  precioResuelto?: PrecioResuelto | null;
}

/**
 * Importe del renglón, o `null` si todavía no tiene precio.
 *
 * El renglón de catálogo **no recalcula**: usa el `total` que resolvió el
 * worker. Recalcularlo acá duplicaría la regla de `esImporteFijo` y las dos
 * copias divergirían.
 */
export function subtotalRenglon(renglon: RenglonConImporte): number | null {
  if (renglon.precioUnitario !== undefined) return renglon.precioUnitario * renglon.cantidad;
  if (!renglon.precioResuelto) return null;
  return Number(renglon.precioResuelto.total);
}

/**
 * Si el renglón se puede guardar.
 *
 * **El precio no es requisito**, y eso es la regla de `imprenta`, no un olvido:
 * una consulta se arma antes de que exista la tarifa —de eso se trata cotizar—
 * y el renglón sin precio se guarda igual, dejando el total en «pendiente».
 * Exigir tarifa obligaría a escribir un importe a mano para poder seguir, que
 * es justo lo que el catálogo vino a evitar.
 */
export function renglonListoParaGuardar(renglon: {
  productoId: string | null;
  cantidad: number;
  /** Se acepta y se ignora: el precio no decide si el renglón se puede guardar. */
  precioResuelto?: PrecioResuelto | null;
}): boolean {
  return Boolean(renglon.productoId) && Number.isFinite(renglon.cantidad) && renglon.cantidad >= 1;
}

/**
 * El primer renglón que falta completar, o `null` si están todos.
 *
 * Devuelve el renglón —y no un booleano— porque la pantalla necesita **cuál**:
 * ahí va el foco. Y devuelve el PRIMERO por lo mismo: saltar al último
 * desorienta cuando hay varios a medio cargar.
 */
export function primerRenglonIncompleto<T extends { productoId: string | null; cantidad: number }>(
  items: readonly T[],
): T | null {
  return items.find((item) => !renglonListoParaGuardar(item)) ?? null;
}

/** Un renglón armado en la pantalla, listo para viajar. */
export interface RenglonArmado extends RenglonConImporte {
  clave: string;
  productoId: string | null;
  productoNombre: string;
  tamanoId: string | null;
  baseId: string | null;
}

/**
 * El renglón como lo espera el worker.
 *
 * Saca lo que es de la pantalla —la clave de React, el precio que se mostró— y
 * conserva el **nombre libre**, que sin `productoId` es lo único que identifica
 * lo cotizado. Descartarlo dejaba renglones anónimos en la base.
 *
 * El precio resuelto NO viaja: el worker lo vuelve a resolver y congela el suyo
 * (ADR 0012). Mandarlo sugeriría que el cliente puede fijar el precio.
 */
export function cuerpoDeRenglon(renglon: RenglonArmado): Record<string, unknown> {
  const { clave: _clave, productoNombre, precioResuelto: _precio, ...resto } = renglon;
  return renglon.productoId ? resto : { ...resto, productoNombre };
}

/**
 * Si un `precioSnapshot` sirve para mostrar el importe del renglón.
 *
 * Viaja como `jsonb`, o sea `unknown`. Confiar en su forma es cómo un snapshot
 * incompleto termina pintando `$NaN` — y arrastrando el total del presupuesto,
 * porque `NaN` contamina la suma entera.
 */
export function esSnapshot(valor: unknown): valor is PrecioResuelto {
  if (typeof valor !== "object" || valor === null) return false;
  const s = valor as Record<string, unknown>;
  return typeof s.total === "string" && typeof s.precio === "string";
}

/** Lo que se tipea en el formulario del renglón, todo como texto. */
export interface RenglonEnCarga {
  tamanoId: string;
  baseId: string;
  cantidad: string;
}

/** Los parámetros de `GET /precios/resolver`. */
export interface ConsultaDeTarifa {
  tamanoId: string;
  baseId: string;
  cantidad: number;
  /** `null` = todavía no se eligió cliente: se cotiza a precio base, sin VIP. */
  clienteId: string | null;
}

/**
 * Qué consultarle a la tarifa, o `null` si todavía no hay nada que preguntar.
 *
 * Existe separada del hook para que la decisión de **no** consultar se pueda
 * probar sin red: es la que evita que media combinación cargada se muestre como
 * «sin tarifa vigente», que es una respuesta distinta a «no pregunté».
 */
export function consultaDeTarifa(
  renglon: RenglonEnCarga,
  clienteId: string,
): ConsultaDeTarifa | null {
  if (!renglon.tamanoId || !renglon.baseId) return null;
  const cantidad = Number.parseInt(renglon.cantidad, 10);
  if (!Number.isInteger(cantidad) || cantidad < 1) return null;
  return {
    tamanoId: renglon.tamanoId,
    baseId: renglon.baseId,
    cantidad,
    clienteId: clienteId || null,
  };
}

/** Lo que la pantalla sabe del precio del renglón que se está cargando. */
export type EstadoDeTarifa =
  | { estado: "buscando" }
  | { estado: "encontrado"; precio: PrecioResuelto }
  | { estado: "sin-tarifa" }
  | { estado: "error"; mensaje: string };

/** La demora de `imprenta`: corta para no molestar, larga para no inundar. */
const DEMORA_MS = 300;

/**
 * Dispara la consulta de tarifa cuando la persona deja de tipear, y devuelve
 * la función que la cancela.
 *
 * La cancelación es lo importante, no el ahorro de requests: dos consultas en
 * vuelo pueden volver **al revés** y dejar en pantalla el precio de la cantidad
 * anterior. Se ve perfectamente válido, y es de otra consulta. Por eso lo que
 * llega después de cancelar se descarta en vez de avisarse.
 */
export function iniciarConsultaDeTarifa(
  consulta: ConsultaDeTarifa,
  opciones: {
    buscar: (consulta: ConsultaDeTarifa, signal: AbortSignal) => Promise<PrecioResuelto | null>;
    alCambiar: (estado: EstadoDeTarifa) => void;
    demoraMs?: number;
  },
): () => void {
  const { buscar, alCambiar, demoraMs = DEMORA_MS } = opciones;
  const abortador = new AbortController();
  let cancelado = false;

  // El aviso sale YA, no al vencer el debounce: si esperara, la pantalla se
  // quedaría un tercio de segundo mostrando «Seleccione tamano y base» con la
  // combinación ya completa — información vieja. Es lo que hace `imprenta`.
  alCambiar({ estado: "buscando" });

  const temporizador = setTimeout(() => {
    buscar(consulta, abortador.signal)
      .then((precio) => {
        if (cancelado) return;
        alCambiar(precio ? { estado: "encontrado", precio } : { estado: "sin-tarifa" });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        alCambiar({
          estado: "error",
          mensaje: err instanceof Error ? err.message : "No se pudo consultar la tarifa",
        });
      });
  }, demoraMs);

  return () => {
    cancelado = true;
    clearTimeout(temporizador);
    abortador.abort();
  };
}

/**
 * Total del presupuesto, o `null` si **algún** renglón no tiene precio.
 *
 * Es la regla de `imprenta`: con un renglón sin cotizar, la suma de los otros
 * no es «el total todavía incompleto», es un número más chico que el que se va
 * a cobrar — y en pantalla se ve igual de definitivo. El worker hace lo mismo
 * del otro lado: guarda `total = NULL`.
 */
export function totalPresupuesto(items: readonly RenglonConImporte[]): number | null {
  let total = 0;
  for (const item of items) {
    const subtotal = subtotalRenglon(item);
    if (subtotal === null) return null;
    total += subtotal;
  }
  return total;
}
